import { Box, ButtonBase, Typography } from "@mui/material";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  AttentionCircleIcon,
  CopyIcon,
} from "../../icons";
import { CurrencyIcon, Skeleton } from "../../components/shared";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  DEPOSIT_NETWORKS,
  fetchNetworks,
  getNetworkById,
} from "../../components/pages/wallet/networks";
import { getOrCreateCryptoWallet } from "../../services/crypto";
import { FeatureGuard } from "../../components/shared";

/**
 * Реальный QR-код через api.qrserver.com. Если картинка не грузится —
 * показываем декоративный SVG-заглушку, чтобы страница не «пустела».
 */
function RealQrCode({ data, size = 200 }: { data: string | null; size?: number }) {
  if (!data) return <QrPlaceholder size={size} />;
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}&margin=1`;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        padding: "12px",
        borderRadius: "16px",
        backgroundColor: "#fff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
      }}
    >
      <Box
        component="img"
        src={url}
        alt="Deposit QR"
        sx={{ width: "100%", height: "100%", display: "block" }}
      />
    </Box>
  );
}

function QrPlaceholder({ size = 200 }: { size?: number }) {
  const modules = 21;
  const cellSize = size / modules;

  const cells = useMemo(() => {
    const result: Array<{ x: number; y: number }> = [];
    let seed = 42;
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };

    for (let y = 0; y < modules; y++) {
      for (let x = 0; x < modules; x++) {
        const isCorner =
          (x < 7 && y < 7) ||
          (x >= modules - 7 && y < 7) ||
          (x < 7 && y >= modules - 7);
        if (isCorner) continue;
        if (random() > 0.55) {
          result.push({ x, y });
        }
      }
    }
    return result;
  }, [modules]);

  const FinderPattern = ({ x, y }: { x: number; y: number }) => (
    <>
      <rect
        x={x * cellSize}
        y={y * cellSize}
        width={cellSize * 7}
        height={cellSize * 7}
        fill="#000"
      />
      <rect
        x={(x + 1) * cellSize}
        y={(y + 1) * cellSize}
        width={cellSize * 5}
        height={cellSize * 5}
        fill="#fff"
      />
      <rect
        x={(x + 2) * cellSize}
        y={(y + 2) * cellSize}
        width={cellSize * 3}
        height={cellSize * 3}
        fill="#000"
      />
    </>
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        padding: "12px",
        borderRadius: "16px",
        backgroundColor: "#fff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
      }}
    >
      <svg
        width={size - 24}
        height={size - 24}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width={size} height={size} fill="#fff" />
        {cells.map((c) => (
          <rect
            key={`${c.x}-${c.y}`}
            x={c.x * cellSize}
            y={c.y * cellSize}
            width={cellSize}
            height={cellSize}
            fill="#000"
          />
        ))}
        <FinderPattern x={0} y={0} />
        <FinderPattern x={modules - 7} y={0} />
        <FinderPattern x={0} y={modules - 7} />
      </svg>

      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "42px",
          height: "42px",
          borderRadius: "50%",
          bgcolor: "#26A17B",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "3px solid #fff",
        }}
      >
        <Typography
          sx={{
            color: "#fff",
            fontSize: "24px",
            fontWeight: 700,
            fontFamily: "Inter",
            lineHeight: 1,
          }}
        >
          ₮
        </Typography>
      </Box>
    </Box>
  );
}

function InfoRow({
  label,
  value,
  copyable,
  highlight,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  highlight?: boolean;
}) {
  const { t } = useTranslation("deposit");
  const handleCopy = () => {
    if (!copyable) return;
    navigator.clipboard?.writeText(value);
    enqueueSnackbar(t("toasts.address_copied"), { variant: "success" });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        gap: "12px",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "text.secondary",
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <ButtonBase
        onClick={handleCopy}
        disabled={!copyable}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          textAlign: "right",
          maxWidth: "65%",
          borderRadius: "6px",
          padding: "2px 4px",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: highlight ? "primary.main" : "text.primary",
            wordBreak: "break-all",
            lineHeight: 1.25,
            textAlign: "right",
          }}
        >
          {value}
        </Typography>
        {copyable && (
          <Box sx={{ color: "primary.main", display: "flex", flexShrink: 0 }}>
            <CopyIcon />
          </Box>
        )}
      </ButtonBase>
    </Box>
  );
}

function DepositSkeleton() {
  return (
    <>
      <Skeleton
        variant="rounded"
        width={220}
        height={26}
        sx={{ borderRadius: "8px", margin: "0 auto 6px", display: "block" }}
      />

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Skeleton
          variant="rounded"
          width={210}
          height={210}
          sx={{ borderRadius: "16px" }}
        />
      </Box>

      <Skeleton variant="rounded" height={72} sx={{ borderRadius: "18px" }} />

      <Skeleton variant="rounded" height={110} sx={{ borderRadius: "18px" }} />

      <Skeleton variant="rounded" height={110} sx={{ borderRadius: "18px" }} />

      <Skeleton
        variant="rounded"
        height={44}
        sx={{ borderRadius: "40px", marginTop: "8px" }}
      />
    </>
  );
}

function DepositPageContent() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("deposit");
  const { data: networks, isLoading: networksLoading } = useQuery({
    queryFn: fetchNetworks,
    queryKey: ["networks"],
    staleTime: 60_000,
  });
  const list = networks && networks.length > 0 ? networks : DEPOSIT_NETWORKS;
  const network = useMemo(
    () =>
      getNetworkById(
        searchParams.get("network"),
        list,
        searchParams.get("currency"),
      ),
    [searchParams, list],
  );

  /**
   * Автоматически получаем/создаём депозит-адрес из DV.net при заходе на страницу.
   * На один пары (userId, network) кошелёк создаётся один раз и переиспользуется.
   */
  const {
    data: wallet,
    isLoading: walletLoading,
    isError: walletError,
    error: walletErrorObj,
    refetch,
  } = useQuery({
    queryFn: () => getOrCreateCryptoWallet(network.id, network.currency),
    queryKey: ["crypto-wallet", network.id, network.currency],
    enabled: Boolean(network.id) && Boolean(network.currency),
    retry: 0,
    staleTime: 60_000 * 30,
  });

  const address = wallet?.address ?? "";
  const displayAddress = address || t("address_unavailable");
  const isLoading = networksLoading || walletLoading;

  const errorText = useMemo(() => {
    if (!walletError) return "";
    const anyErr = walletErrorObj as any;
    return (
      anyErr?.response?.data?.message ??
      anyErr?.message ??
      t("error_default")
    );
  }, [walletError, walletErrorObj, t]);

  const handleCopy = () => {
    if (!address) return;
    navigator.clipboard?.writeText(address);
    enqueueSnackbar(t("toasts.address_copied"), { variant: "success" });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        marginTop: "20px",
        marginBottom: "140px",
      }}
    >
      {isLoading ? (
        <DepositSkeleton />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              marginBottom: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {t("title")}
            </Typography>
            <Box
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 14px 6px 6px",
                borderRadius: "999px",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.06)",
              })}
            >
              <CurrencyIcon
                value={network.logoUrl}
                size={22}
                fallbackLetter={network.standard || network.id}
                alt={network.name}
              />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                {network.fullName}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <RealQrCode data={address || null} size={210} />
          </Box>

          {walletError && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "14px 16px",
                borderRadius: "18px",
                bgcolor: "secondary.main",
                border: (theme) => `1px solid ${theme.palette.error.main}`,
              }}
            >
              <Box sx={{ flexShrink: 0, color: "error.main" }}>
                <AttentionCircleIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: "2px" }}>
                  {t("error_title")}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", color: "text.secondary", lineHeight: 1.35 }}
                >
                  {errorText}
                </Typography>
              </Box>
              <ButtonBase
                onClick={() => refetch()}
                sx={{
                  padding: "6px 12px",
                  borderRadius: "12px",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {t("retry")}
              </ButtonBase>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 16px",
              borderRadius: "18px",
              bgcolor: "secondary.main",
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <AttentionCircleIcon />
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 600, mb: "2px" }}
              >
                {t("attention_title")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "text.secondary",
                  lineHeight: 1.35,
                }}
              >
                {t("attention_hint", {
                  code: network.currency,
                  network: network.standard,
                })}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: "18px",
              bgcolor: "secondary.main",
              overflow: "hidden",
              "& > *:not(:last-child)": {
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <InfoRow
              label={t("address_of_wallet")}
              value={displayAddress}
              copyable={Boolean(address)}
              highlight
            />
            <InfoRow label={t("network_label")} value={network.fullName} />
            {wallet?.memo && (
              <InfoRow
                label={t("memo_optional_label")}
                value={wallet.memo}
                copyable
              />
            )}
          </Box>

          <Box
            sx={{
              borderRadius: "18px",
              bgcolor: "secondary.main",
              overflow: "hidden",
              "& > *:not(:last-child)": {
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <InfoRow label={t("min_amount_label")} value={network.minAmount} />
            <InfoRow label={t("network_fee_label")} value={network.platformFee} />
          </Box>

          <ButtonBase
            disabled={!address}
            onClick={handleCopy}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              padding: "14px 20px",
              borderRadius: "40px",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: "15px",
              fontWeight: 500,
              marginTop: "8px",
              opacity: !address ? 0.5 : 1,
            }}
          >
            <CopyIcon />
            {t("copy_address")}
          </ButtonBase>
        </>
      )}
    </Box>
  );
}

export function DepositPage() {
  return (
    <FeatureGuard feature="deposit">
      <DepositPageContent />
    </FeatureGuard>
  );
}
